import { baseDatabaseSchema } from '@domain/schemas/database.schema';
import { z } from 'zod';

import { ApiPatchOperation } from './api-patch-operation';
import { DbSubmissionBlockSchema } from './block.schema';
import { DbSectionSchema } from './section.schema';

export const summaryBlockSchema = DbSubmissionBlockSchema.pick({
  attachment_id: true,
  content: true,
  helperText: true,
  linkedDatapoints: true,
  name: true,
  section_id: true,
  shouldInsertPageBreak: true,
  title: true,
  type: true,
  repeaterIds: true,
  isVisible: true,
});

export const summaryBlockCreateSchema = summaryBlockSchema.extend({
  order: z.number().default(0).optional(),
  operation: z.literal(ApiPatchOperation.CreateSummaryBlock),
});

export const summaryBlockUpdateSchema = summaryBlockSchema
  .partial()
  .omit({ section_id: true })
  .extend({
    blockId: z.string(),
    operation: z.literal(ApiPatchOperation.UpdateSummaryBlock),
  });

export const summaryBlockDeleteSchema = z.object({
  blockId: z.string(),
  operation: z.literal(ApiPatchOperation.DeleteSummaryBlock),
});

export const summaryBlockHideFromPreviewSchema = z.object({
  blockId: z.string(),
  operation: z.literal(ApiPatchOperation.HideBlockFromPreview),
});

export const summaryBlockShowInPreviewSchema = z.object({
  blockId: z.string(),
  operation: z.literal(ApiPatchOperation.ShowBlockInPreview),
});

export const DbSummaryBlockSchema = summaryBlockSchema.merge(baseDatabaseSchema);

const summarySectionPositionSchema = z.enum(['start', 'end']);

export const summarySectionSchema = DbSectionSchema.pick({
  label: true,
  layout: true,
  should_reset_page_counter: true,
  should_hide_title_in_pdf: true,
  standardised_name: true,
}).extend({
  id: z.string().optional(),
  position: summarySectionPositionSchema.default('end'),
});

export const DbSummarySectionSchema = summarySectionSchema.merge(baseDatabaseSchema);

export const ENDORSEMENT_DETAILS_SENTINEL = 'endorsement_details' as const;
export const summarySectionDeleteSchema = z.object({
  sectionId: z.string(),
  operation: z.literal(ApiPatchOperation.DeleteSummarySection),
});

export const summaryBlockReorderSchema = z.object({
  blockId: z.string(),
  order: z.number().int().min(0),
  targetSectionId: z.string().uuid().optional(),
  operation: z.literal(ApiPatchOperation.ReorderSummaryBlock),
});

export const summarySectionReorderSchema = z.object({
  sectionId: z.union([z.string().uuid(), z.literal(ENDORSEMENT_DETAILS_SENTINEL)]),
  insertBeforeId: z.string().nullable(),
  operation: z.literal(ApiPatchOperation.ReorderSummarySection),
});

export const restoreBlocksHiddenFromPreviewSchema = z.object({
  operation: z.literal(ApiPatchOperation.RestoreBlocksHiddenFromPreview),
});

export type SummaryBlockCreate = z.infer<typeof summaryBlockCreateSchema>;
export type SummaryBlockUpdate = z.infer<typeof summaryBlockUpdateSchema>;
export type SummaryBlockDelete = z.infer<typeof summaryBlockDeleteSchema>;
export type SummaryBlockDb = z.infer<typeof DbSummaryBlockSchema>;
export type SummaryBlockHideFromPreview = z.infer<typeof summaryBlockHideFromPreviewSchema>;
export type SummaryBlockShowInPreview = z.infer<typeof summaryBlockShowInPreviewSchema>;
export type RestoreBlocksHiddenFromPreview = z.infer<typeof restoreBlocksHiddenFromPreviewSchema>;

export type SummarySection = z.infer<typeof summarySectionSchema>;
export type SummarySectionDb = z.infer<typeof DbSummarySectionSchema>;
export type SummarySectionDelete = z.infer<typeof summarySectionDeleteSchema>;
export type SummarySectionPosition = z.infer<typeof summarySectionPositionSchema>;
export type SummaryBlockReorder = z.infer<typeof summaryBlockReorderSchema>;
export type SummarySectionReorder = z.infer<typeof summarySectionReorderSchema>;
